import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Undo2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import wqmsService from "../services/wqmsService";

// ---------------- helpers ----------------
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, delta) => new Date(d.getFullYear(), d.getMonth() + delta, 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

const fmtMonthShort = (d) => d.toLocaleString("en-US", { month: "long" });
const fmtYear = (d) => d.getFullYear();

const buildCalendarCells = (year, monthIndex0) => {
  const first = new Date(year, monthIndex0, 1);
  const last = new Date(year, monthIndex0 + 1, 0);
  const firstDow = first.getDay(); // 0=Sun
  const daysInMonth = last.getDate();

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, monthIndex0, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

// same legend bands you already use
const wqiColor = (wqi) => {
  if (wqi == null) return "#9ca3af";     // No data (gray)
  if (wqi <= 25)  return "#22c55e";      // Excellent
  if (wqi <= 50)  return "#16a34a";      // Good
  if (wqi <= 75)  return "#eab308";      // Poor
  if (wqi <= 100) return "#ef4444";      // Very Poor
  return "#7f1d1d";                      // Unsuitable
};

const isoKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const computeStats = (rows) => {
  const vals = rows.map(r => r.wqi).filter(v => typeof v === "number");
  if (!vals.length) return { max: null, min: null, avg: null, maxDate: null, minDate: null };

  let max = -Infinity, min = Infinity;
  let maxDate = null, minDate = null;

  for (const r of rows) {
    if (typeof r.wqi !== "number") continue;
    if (r.wqi > max) { max = r.wqi; maxDate = r.date; }
    if (r.wqi < min) { min = r.wqi; minDate = r.date; }
  }
  const avg = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  return { max, min, avg, maxDate, minDate };
};

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

const ResponsiveStyles = () => (
  <style>{`
    .page-bg{
      min-height: 100vh;
      background: linear-gradient(165deg, #e8f4fc 0%, #f0f4ff 28%, #faf5ff 55%, #f0f9ff 100%);
      background-attachment: fixed;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
      color: #111827;
    }
    .topbar{
      position: sticky;
      top: 0;
      z-index: 5;
      background: linear-gradient(135deg, #eff6ff 0%, #f5f3ff 50%, #fdf4ff 100%);
      border-bottom: 1px solid #dbeafe;
    }
    .container{
      max-width: 1400px;
      margin: 0 auto;
      padding: 14px 16px 30px;
    }

    .toolbar{
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
      margin-top: 10px;
    }
    .select{
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 10px 12px;
      background: #fff;
      font-weight: 600;
      font-family: inherit;
      color: #111827;
      min-width: 220px;
    }
    .select:focus{
      outline: none;
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37,99,235,.18);
    }
    .navbtn{
      width: 44px;
      height: 44px;
      border-radius: 12px;
      border: 1px solid #e5e7eb;
      background: #fff;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #111827;
    }
    .navbtn:hover{
      background: #f9fafb;
      border-color: #d1d5db;
    }
    .months-row{
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 16px;
      margin-top: 16px;
    }
    .card{
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 8px 24px rgba(0,0,0,.06);
    }
    .card-head{
      padding: 14px 14px 8px;
      display: flex;
      justify-content: space-between;
      align-items: baseline;
    }
    .month-name{
      font-size: 22px;
      font-weight: 500;
      color: #111827;
    }
    .year{
      font-size: 18px;
      font-weight: 800;
      color: #111827;
      opacity: .7;
    }
    .stats{
      padding: 0 14px 12px;
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 10px;
      align-items: end;
    }
    .stat-label{
      font-size: 12px;
      color: #6b7280;
      font-weight: 700;
    }
    .stat-val{
      font-size: 26px;
      font-weight: 500;
      color: #111827;
      line-height: 1.1;
    }
    .bar{
      height: 8px;
      border-radius: 999px;
      background: #e5e7eb;
      overflow: hidden;
      margin-top: 6px;
    }
    .bar > div{
      height: 100%;
      border-radius: 999px;
    }
    .bar-note{
      font-size: 11px;
      color: #9ca3af;
      margin-top: 6px;
      text-align: center;
    }
    .dow{
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 6px;
      padding: 10px 14px 0;
      color: #9ca3af;
      font-size: 12px;
      font-weight: 800;
      text-align: center;
    }
    .grid{
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 6px;
      padding: 8px 14px 14px;
    }
    .cell{
      height: 54px;
      border-radius: 10px;
      border: 1px solid #eef2f7;
      background: #f9fafb;
      position: relative;
      overflow: hidden;
    }
    .cell.empty{
      background: #fff;
      border: 1px solid transparent;
    }
    .cell .day{
      position: absolute;
      top: 6px;
      left: 8px;
      font-size: 12px;
      font-weight: 800;
      color: rgba(255,255,255,.95);
      text-shadow: 0 1px 2px rgba(0,0,0,.25);
    }
    .cell .val{
      position: absolute;
      bottom: 6px;
      left: 8px;
      right: 8px;
      font-size: 12px;
      font-weight: 900;
      color: rgba(255,255,255,.95);
      text-shadow: 0 1px 2px rgba(0,0,0,.25);
      text-align: right;
    }

    @media (max-width: 1100px){
      .months-row{ grid-template-columns: 1fr; }
    }
  `}</style>
);

// ---------------- component ----------------
const MonthlyWqiTrends = () => {
  const nav = useNavigate();
  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState("");
  const [anchor, setAnchor] = useState(startOfMonth(new Date()));

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  // store data for 3 months: key = "YYYY-MM"
  const [monthData, setMonthData] = useState({});

  // load stations
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      const list = await wqmsService.getStations({ timeRange: "all", force: true, signal: ac.signal });
      setStations(list || []);
      if (!stationId && list?.length) setStationId(String(list[0].id));
    })().catch(console.error);
    return () => ac.abort();
  }, []);

  const station = stations.find(s => String(s.id) === String(stationId));

  // build 3 visible months like AQI: prev, current, next
  const visibleMonths = useMemo(() => ([
    addMonths(anchor, -1),
    anchor,
    addMonths(anchor, +1),
  ]), [anchor]);

  // fetch 3 months whenever station or anchor changes
  useEffect(() => {
    if (!stationId) return;
    let stop = false;

    (async () => {
      try {
        setLoading(true);
        setErr("");

        const reqs = visibleMonths.map(d => {
          const y = d.getFullYear();
          const m = d.getMonth() + 1;
          return wqmsService.getMonthlyWqi(stationId, y, m).then(rows => ({
            key: `${y}-${String(m).padStart(2, "0")}`,
            rows: Array.isArray(rows) ? rows : []
          }));
        });

        const results = await Promise.all(reqs);
        if (stop) return;

        const next = {};
        for (const r of results) next[r.key] = r.rows;
        setMonthData(next);

      } catch (e) {
        console.error(e);
        setErr("Failed to load monthly WQI");
      } finally {
        if (!stop) setLoading(false);
      }
    })();

    return () => { stop = true; };
  }, [stationId, visibleMonths]);

    // --------- Build Bar Chart Data for any visible month (prev/current/next) ----------
  const buildBarForMonth = useMemo(() => {
    return (d) => {
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const monthKey = `${y}-${String(m).padStart(2, "0")}`;
      const rows = monthData[monthKey] || [];

      const map = new Map();
      for (const r of rows) map.set(r.date, r.wqi);

      const days = endOfMonth(d).getDate();
      const mm2 = String(m).padStart(2, "0");

      const barData = Array.from({ length: days }, (_, i) => {
        const day = i + 1;
        const dd2 = String(day).padStart(2, "0");
        const dateKey = `${y}-${mm2}-${dd2}`;
        const wqi = map.get(dateKey) ?? null;
        return { day, key: dateKey, wqi };
      });

      const vals = barData.map(x => x.wqi).filter(v => typeof v === "number");
      const avg = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;

      return {
        days,
        barData,
        avg,
        title: d.toLocaleString("en-US", { month: "long", year: "numeric" }),
      };
    };
  }, [monthData]);


  return (
    <div className="page-bg">
      <ResponsiveStyles />

      <div className="topbar">
        <div className="container">
          <div className="mwqi-header-top">
            <button type="button" className="admin-btn admin-btn-secondary" onClick={() => nav("/dashboard")}>
              <Undo2 size={18} /> Back
            </button>

            <div className="mwqi-title-block">
              <CalendarIcon size={22} />
              <h1>Monthly WQI</h1>
            </div>

            <div className="mwqi-header-actions">
              <select
                className="select"
                value={stationId}
                onChange={(e) => setStationId(String(e.target.value))}
                title="Select station"
              >
                {stations.map(s => (
                  <option key={String(s.id)} value={String(s.id)}>
                    {s.site_name || `WQMS-${s.id}`}
                  </option>
                ))}
              </select>

              <div className="mwqi-month-nav">
                <button type="button" className="navbtn" onClick={() => setAnchor(addMonths(anchor, -1))} title="Previous">
                  <ChevronLeft />
                </button>
                <button type="button" className="navbtn" onClick={() => setAnchor(addMonths(anchor, +1))} title="Next">
                  <ChevronRight />
                </button>
              </div>
            </div>
          </div>

          <p className="mwqi-subtitle">
            {station ? `Station: ${station.site_name}${station.district ? ` — ${station.district}` : ""}` : "Select a station"}
          </p>

          <div className="mwqi-legend">
            {[
              { c: "#22c55e", t: "Excellent (0–25)" },
              { c: "#16a34a", t: "Good (26–50)" },
              { c: "#eab308", t: "Poor (51–75)" },
              { c: "#ef4444", t: "Very Poor (76–100)" },
              { c: "#7f1d1d", t: "Unsuitable (>100)" },
              { c: "#718096", t: "No data" },
            ].map((it) => (
              <span key={it.t} className="mwqi-legend-item">
                <span className="mwqi-legend-dot" style={{ background: it.c }} />
                {it.t}
              </span>
            ))}
          </div>

          <div className="mwqi-formula">
            <strong>WQI (US-EPA Weighted Arithmetic):</strong>{" "}
            Computed as{" "}
            <span style={{ fontFamily: "monospace" }}>Σ(Qi × Wi) / Σ(Wi)</span>, where{" "}
            <span style={{ fontFamily: "monospace" }}>Wi = 1 / Si</span>.{" "}
            <em>Lower WQI indicates better water quality.</em>
          </div>

          {err && (
            <div style={{ marginTop: 10, background: "#fee2e2", border: "1px solid #fecaca", padding: 10, borderRadius: 12, color: "#991b1b", fontWeight: 700 }}>
              {err}
            </div>
          )}
        </div>
      </div>

            <div className="container">
        {loading ? (
          <div style={{ padding: 30, fontWeight: 800, color: "#374151" }}>Loading…</div>
        ) : (
          <>
            {/* 3 month cards */}
            <div className="months-row">
              {visibleMonths.map((d) => {
                const y = d.getFullYear();
                const m = d.getMonth() + 1;
                const monthKey = `${y}-${String(m).padStart(2, "0")}`;
                const rows = monthData[monthKey] || [];

                const stats = computeStats(rows);
                const cells = buildCalendarCells(y, d.getMonth());

                const map = new Map();
                for (const r of rows) map.set(r.date, r.wqi);

                const pct = (v) =>
                  v == null ? 0 : Math.max(0, Math.min(100, Math.round((v / 100) * 100)));

                return (
                  <div className="card" key={monthKey}>
                    <div className="card-head">
                      <div className="month-name">{fmtMonthShort(d)}</div>
                      <div className="year">{fmtYear(d)}</div>
                    </div>

                    <div className="stats">
                      <div>
                        <div className="stat-label">Max</div>
                        <div className="stat-val">{stats.max ?? "—"}</div>
                        <div className="bar">
                          <div style={{ width: `${pct(stats.max)}%`, background: wqiColor(stats.max) }} />
                        </div>
                        <div className="bar-note">
                          {stats.maxDate ? new Date(stats.maxDate).toLocaleDateString("en-GB") : ""}
                        </div>
                      </div>

                      <div>
                        <div className="stat-label">Min</div>
                        <div className="stat-val">{stats.min ?? "—"}</div>
                        <div className="bar">
                          <div style={{ width: `${pct(stats.min)}%`, background: wqiColor(stats.min) }} />
                        </div>
                        <div className="bar-note">
                          {stats.minDate ? new Date(stats.minDate).toLocaleDateString("en-GB") : ""}
                        </div>
                      </div>

                      <div>
                        <div className="stat-label">Average</div>
                        <div className="stat-val">{stats.avg ?? "—"}</div>
                        <div className="bar">
                          <div style={{ width: `${pct(stats.avg)}%`, background: wqiColor(stats.avg) }} />
                        </div>
                        <div className="bar-note">Monthly Avg</div>
                      </div>
                    </div>

                    <div className="dow">
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((x) => (
                        <div key={x}>{x}</div>
                      ))}
                    </div>

                    <div className="grid">
                      {cells.map((dt, idx) => {
                        if (!dt) return <div key={`e-${idx}`} className="cell empty" />;

                        const key = isoKey(dt);
                        const wqi = map.get(key) ?? null;
                        const bg = wqiColor(wqi);

                        return (
                          <div
                            key={key}
                            className="cell"
                            title={`${key} — ${wqi == null ? "No data" : `WQI: ${wqi}`}`}
                            style={{ background: bg }}
                          >
                            <div className="day">{dt.getDate()}</div>
                            <div className="val">{wqi == null ? "—" : wqi}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bottom Monthly Trend Bar Charts (ALL 3 months) */}
            {visibleMonths.map((d) => {
              const { days, barData, avg, title } = buildBarForMonth(d);
              const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

              return (
                <div
                  key={`bar-${monthKey}`}
                  style={{
                    marginTop: 16,
                    background: "rgba(255,255,255,.92)",
                    border: "1px solid #e5e7eb",
                    borderRadius: 14,
                    padding: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ fontWeight: 800, color: "#111827", fontSize: 14 }}>
                      Monthly WQI Trend (Bar Chart) — {title}
                    </div>
                    <div style={{ fontSize: 12, color: "#4b5563" }}>
                      {avg == null ? "Average: —" : `Average: ${avg}`}
                    </div>
                  </div>

                  <div style={{ position: "relative", marginTop: 10 }}>
                    {[100, 50, 0].map((v) => (
                      <div
                        key={v}
                        style={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          top: `${(1 - v / 100) * 100}%`,
                          borderTop: "1px dashed rgba(17,24,39,.18)",
                          pointerEvents: "none",
                        }}
                      />
                    ))}
                    <div style={{ position: "absolute", left: 6, top: 2, fontSize: 10, color: "#6b7280" }}>100</div>
                    <div
                      style={{
                        position: "absolute",
                        left: 6,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: 10,
                        color: "#6b7280",
                      }}
                    >
                      50
                    </div>
                    <div style={{ position: "absolute", left: 6, bottom: 2, fontSize: 10, color: "#6b7280" }}>0</div>

                    <div
                      style={{
                        height: 160,
                        display: "flex",
                        alignItems: "flex-end",
                        gap: 4,
                        padding: "10px 8px 18px 34px",
                        borderRadius: 12,
                        background: "rgba(255,255,255,.7)",
                        border: "1px solid rgba(0,0,0,.06)",
                        overflow: "hidden",
                      }}
                    >
                      {barData.map(({ day, key, wqi }) => {
                        const color = wqiColor(wqi);
                        const h =
                          wqi == null ? 3 : Math.round((clamp(wqi, 0, 100) / 100) * 120) + 6;
                        const showLabel = day === 1 || day === days || day % 5 === 0;

                        return (
                          <div key={key} style={{ flex: 1, minWidth: 3, position: "relative" }}>
                            <div
                              title={`${key} — ${wqi == null ? "No data" : `WQI: ${wqi}`}`}
                              style={{
                                height: h,
                                borderRadius: "8px 8px 3px 3px",
                                background: color,
                                opacity: wqi == null ? 0.35 : 0.85,
                                border: "1px solid rgba(0,0,0,.08)",
                              }}
                            />
                            {showLabel && (
                              <div
                                style={{
                                  position: "absolute",
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                  bottom: -16,
                                  fontSize: 9.5,
                                  color: "#6b7280",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {day}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ marginTop: 6, fontSize: 11, color: "#6b7280", paddingLeft: 34 }}>
                      X-axis: Day of month • Y-axis: WQI (0–100)
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};


export default MonthlyWqiTrends;
